import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sprout, Loader2 } from "lucide-react";
import { toast } from "sonner";

const bgImg = "https://images.unsplash.com/photo-1508175688576-0c076b47b5b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxsdXNoJTIwYWdyaWN1bHR1cmFsJTIwZmFybSUyMGZpZWxkJTIwYWVyaWFsJTIwY3JvcHN8ZW58MHx8fHwxNzg5MTkxODg4fDA&ixlib=rb-4.1.0&q=85";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", farm_name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = isLogin
      ? await login(form.email, form.password)
      : await register({ name: form.name, email: form.email, password: form.password, farm_name: form.farm_name });
    setLoading(false);
    if (res.ok) {
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/app");
    } else {
      toast.error(res.error);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF9F6]">
      <div className="hidden lg:block relative">
        <img src={bgImg} alt="Farm fields" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-emerald-50">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[#52B788] flex items-center justify-center"><Sprout className="h-5 w-5 text-emerald-950" /></div>
            <span className="font-display font-extrabold text-lg">AgroTerra</span>
          </Link>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight mb-3">Turn soil data into smarter harvests.</h2>
            <p className="text-emerald-100/80 max-w-sm">Join farmers using AI to read their ground and pick the right crops.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl bg-[#2D6A4F] flex items-center justify-center"><Sprout className="h-5 w-5 text-white" /></div>
            <span className="font-display font-extrabold text-lg text-emerald-950">AgroTerra</span>
          </Link>
          <h1 className="font-display text-3xl font-extrabold text-emerald-950 mb-2">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-stone-600 mb-8">{isLogin ? "Log in to study your soil." : "Start reading your land today."}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Name</Label>
                  <Input data-testid="name-input" required value={form.name} onChange={set("name")} placeholder="Jane Farmer" className="mt-1.5 bg-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Farm name</Label>
                  <Input data-testid="farm-input" value={form.farm_name} onChange={set("farm_name")} placeholder="Green Acres" className="mt-1.5 bg-white" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</Label>
              <Input data-testid="email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@farm.com" className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Password</Label>
              <Input data-testid="password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="••••••••" className="mt-1.5 bg-white" />
            </div>
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-12 bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-xl text-base font-semibold">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : isLogin ? "Log in" : "Create account"}
            </Button>
          </form>

          <p className="text-center text-sm text-stone-600 mt-6">
            {isLogin ? "New to AgroTerra? " : "Already have an account? "}
            <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="text-[#2D6A4F] font-semibold hover:underline">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
